import { useEffect, useState } from 'react';
import type { Transition } from 'framer-motion';

/**
 * Motion design tokens + helpers — the single source of truth for how the app
 * moves. Keeping easings, springs and durations here (instead of inline per
 * component) is what makes every transition feel like it came from the same
 * native toolkit: a sheet, a tab pill and a page push all share the same physics.
 *
 * The curves mirror UIKit's defaults:
 *  - EASE_OUT_IOS — the standard "settle" curve (decelerate into place).
 *  - EASE_IOS     — the symmetric push/pop curve used by UINavigationController.
 */
export const EASE_OUT_IOS = [0.22, 0.61, 0.36, 1] as const;
export const EASE_IOS = [0.32, 0.72, 0, 1] as const;

/**
 * The whole app's duration scale, in **milliseconds**. Every transition picks one
 * of these four steps so motion reads as one system instead of a dozen ad-hoc
 * timings. Page push/pop, the edge-swipe settle and the Tailwind tokens all derive
 * from here (see `nav-stack/constants.ts` and `tailwind.config.js`).
 */
export const DURATION = {
  /** Press feedback, colour fades, toggles. */
  micro: 150,
  /** Pills, cross-fades, tab-content swaps, backdrops. */
  small: 200,
  /** Page push / pop and the edge-swipe commit baseline. */
  page: 340,
  /** Edge-swipe cancel (rubber-band back). */
  cancel: 220,
  /** Full-surface sheet dismiss (crisp tween, no spring tail). */
  sheetOut: 280
} as const;

/** Build a CSS `cubic-bezier(...)` string from an easing tuple (for inline/CSS use). */
export const cssCubicBezier = (e: readonly number[]) =>
  `cubic-bezier(${e[0]}, ${e[1]}, ${e[2]}, ${e[3]})`;
/** CSS string forms of the two iOS curves — the bridge to inline styles + Tailwind. */
export const CSS_EASE_OUT = cssCubicBezier(EASE_OUT_IOS);
export const CSS_EASE_IN_OUT = cssCubicBezier(EASE_IOS);

/** Snappy spring for surfaces that slide in (sheets, panels). */
export const SPRING_SHEET: Transition = {
  type: 'spring',
  stiffness: 420,
  damping: 38,
  mass: 0.9
};

/** Tight spring for small layout moves (active pills, indicators). */
export const SPRING_PILL: Transition = {
  type: 'spring',
  stiffness: 560,
  damping: 42
};

/** Soft spring for playful pops (toasts, badges). */
export const SPRING_POP: Transition = {
  type: 'spring',
  stiffness: 480,
  damping: 30,
  mass: 0.8
};

/**
 * Drag-to-reorder glide — neighbours slide out of the way and the dropped row
 * settles into its slot. Tuned to be snappy yet overshoot-free so a list of
 * rows reflows like a single buttery surface (cycling confidence ladder).
 */
export const SPRING_REORDER: Transition = {
  type: 'spring',
  stiffness: 620,
  damping: 46,
  mass: 0.7
};

/** Page push/pop slide timing (tween — predictable, matches the edge-swipe pop). */
export const PAGE_SLIDE: Transition = { duration: DURATION.page / 1000, ease: EASE_IOS };
export const PAGE_FADE: Transition = { duration: DURATION.small / 1000, ease: EASE_OUT_IOS };

/**
 * Crisp downward dismiss for full-surface sheets — a bounded tween, NOT the present
 * spring. A near-critically-damped spring sliding a full-viewport panel has a long
 * asymptotic tail, so it lingers at the bottom edge before unmounting (the "janky"
 * close). A fixed-duration ease leaves cleanly. Shared by `Sheet` + `CardBuilderSheet`.
 */
export const SHEET_DISMISS: Transition = { duration: DURATION.sheetOut / 1000, ease: EASE_IOS };

/** Synchronous read of the OS "Reduce Motion" preference (safe on web). */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  );
}

/** Reactive media-query hook (re-renders when the query result flips). */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** Reactive "prefers reduced motion" — drives whether components animate at all. */
export function useReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

/** True on the desktop breakpoint (Tailwind `md`). Gates horizontal page slides. */
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 768px)');
}

/**
 * True on the WIDE desktop breakpoint (Tailwind `lg`, 1024px). Use when a layout
 * should only expand at real laptop/desktop widths — e.g. three-column group grids,
 * full-bleed boards. For multi-pane rails and overlay drawers, prefer
 * {@link useIsDesktop} (`md`, 768px+) so iPad portrait gets the same treatment.
 *
 * Deliberately distinct from {@link useIsDesktop}, which owns the chrome flip
 * (SideNav ↔ TabBar) and tablet overlay surfaces.
 */
export function useIsWide(): boolean {
  return useMediaQuery('(min-width: 1024px)');
}
